import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Common";
import { SignOfferDialog, ContractButton, FeeStatus } from "@/components/Signature";
import { api, formatErr, fmtDate, fmtDay, useFetch } from "@/lib/api";

const Fact = ({ k, v }) => <div><dt className="text-[10px] font-mono uppercase tracking-wider text-stone-500">{k}</dt><dd className="font-medium text-stone-900">{v}</dd></div>;

const OfferCard = ({ o, onUpdate }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [signing, setSigning] = useState(false);
  const respond = async (decision) => {
    if (decision === "accept") return setSigning(true);
    setBusy(true);
    try {
      onUpdate((await api.post(`/offers/${o.id}/respond`, { decision, note })).data);
      toast.success("Offer declined");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <div data-testid={`cand-offer-${o.id}`} className="rounded-md border border-stone-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl font-bold">{o.position}</p>
          <Link to={`/candidate/businesses/${o.business_id}`} data-testid={`offer-business-link-${o.id}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-stone-600 underline"><Building2 className="h-4 w-4" />{o.business.name}</Link>
        </div>
        <StatusBadge status={o.status} testId={`cand-offer-status-${o.id}`} />
      </div>
      <dl className="mt-5 grid grid-cols-2 gap-4 border-y border-stone-100 py-4 text-sm sm:grid-cols-4">
        <Fact k="Rate" v={`$${o.hourly_rate}/hr`} /><Fact k="Hours" v={`${o.hours_per_week}/week`} />
        <Fact k="Type" v={o.engagement} /><Fact k="Start" v={fmtDay(o.start_date) || "Flexible"} />
      </dl>
      {o.message && <p className="mt-4 text-sm text-stone-700">{o.message}</p>}
      {(o.license_terms || o.licensing_fee > 0) && (
        <div className="mt-4 rounded-md bg-stone-50 p-4 text-sm">
          <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500">Licence & contract terms{o.licensing_fee > 0 ? ` · placement fee $${o.licensing_fee} (paid by business)` : ""}</p>
          <p className="mt-1 whitespace-pre-wrap text-stone-700">{o.license_terms}</p>
        </div>
      )}
      <p className="mt-3 text-[11px] font-mono text-stone-400">Received {fmtDate(o.created_at)}</p>
      {o.status === "Pending" ? (
        <div className="mt-4 space-y-3">
          <Textarea data-testid={`offer-note-${o.id}`} rows={2} placeholder="Optional note to the business" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-2">
            <Button data-testid={`offer-accept-${o.id}`} disabled={busy} onClick={() => respond("accept")} className="bg-emerald-700 hover:bg-emerald-800">Sign & accept</Button>
            <Button data-testid={`offer-decline-${o.id}`} disabled={busy} variant="outline" onClick={() => respond("decline")}>Decline</Button>
          </div>
        </div>
      ) : o.response_note && <p className="mt-3 text-sm italic text-stone-500">Your note: {o.response_note}</p>}
      {o.contract && <p className="mt-3 text-xs text-stone-500">Signed by {o.contract.signed_name} · <ContractButton offer={o} /></p>}
      <FeeStatus offer={o} />
      <SignOfferDialog offer={o} open={signing} onOpenChange={setSigning} onSigned={onUpdate} />
    </div>
  );
};

export default function CandidateOffers() {
  const { data, setData } = useFetch("/offers");
  return (
    <div>
      <PageHeader eyebrow="Job offers" title="Offers" desc="Review each business and their terms, then accept or decline." />
      {data?.length === 0 && <EmptyState testId="cand-offers-empty" icon={Send} title="No offers yet" desc="When a business sends you an offer it will show up here and in your inbox." />}
      <div className="space-y-4">{(data || []).map((o) => <OfferCard key={o.id} o={o} onUpdate={(n) => setData((p) => p.map((x) => (x.id === n.id ? n : x)))} />)}</div>
    </div>
  );
}
